<template>
  <header>
    <div class="container">
      <nav class="nav-container">
        <router-link to="/" class="logo">
          <img :src="logoUrl" alt="ITMaak TechDev Logo" class="logo-img" v-if="logoUrl">
          <i class="fas fa-code logo-icon" v-else></i>
          <span class="logo-text">ITMaak TechDev</span>
        </router-link>
        
        <button class="mobile-menu-btn" @click="toggleMobileMenu" aria-label="Menu mobile">
          <i :class="mobileMenuIcon"></i>
        </button>
        
        <div class="nav-links" :class="{ active: isMobileMenuOpen }">
          <a href="#home" @click.prevent="scrollToSection('home')">
            <i class="fas fa-home"></i> Accueil
          </a>
          <a href="#articles" @click.prevent="scrollToSection('articles')">
            <i class="fas fa-newspaper"></i> Articles
          </a>
          <a href="#categories" @click.prevent="scrollToSection('categories')">
            <i class="fas fa-tags"></i> Catégories
          </a>
          <a href="#about" @click.prevent="scrollToSection('about')">
            <i class="fas fa-info-circle"></i> À propos
          </a>
          <a href="#contact" @click.prevent="scrollToSection('contact')">
            <i class="fas fa-envelope"></i> Contact
          </a>
          
          <!-- Bouton recherche (optionnel) -->
          <button class="search-btn" @click="toggleSearch" aria-label="Rechercher">
            <i class="fas fa-search"></i>
          </button>
        </div>
      </nav>
    </div>
    
    <!-- Barre de recherche (optionnelle) -->
    <div class="search-container" :class="{ active: isSearchOpen }" v-if="showSearch">
      <div class="container">
        <div class="search-bar">
          <input 
            type="text" 
            v-model="searchQuery" 
            @input="handleSearch"
            placeholder="Rechercher un article..." 
            aria-label="Rechercher un article"
          >
          <button class="search-close" @click="closeSearch" aria-label="Fermer la recherche">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'SiteHeader',
  data() {
    return {
      isMobileMenuOpen: false,
      isSearchOpen: false,
      searchQuery: '',
      showSearch: false,
      logoUrl: null
    }
  },
  computed: {
    mobileMenuIcon() {
      return this.isMobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'
    }
  },
  mounted() {
    // Charger le logo (peut être remplacé par une URL externe)
    this.logoUrl = this.loadLogo()
    
    // Fermer le menu au clic en dehors
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    loadLogo() {
      // Vous pouvez utiliser une image locale ou une URL
      try {
        // Essayer de charger depuis les assets
        return require('@/assets/images/logo.png')
      } catch {
        // Utiliser une URL par défaut
        return 'https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
      }
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
      if (this.isSearchOpen) this.closeSearch()
    },
    toggleSearch() {
      this.isSearchOpen = !this.isSearchOpen
      this.showSearch = true
      if (this.isMobileMenuOpen) this.isMobileMenuOpen = false
      
      // Focus sur le champ de recherche
      if (this.isSearchOpen) {
        this.$nextTick(() => {
          const input = document.querySelector('.search-bar input')
          if (input) input.focus()
        })
      }
    },
    closeSearch() {
      this.isSearchOpen = false
      this.searchQuery = ''
      setTimeout(() => {
        this.showSearch = false
      }, 300)
    },
    handleSearch() {
      // Émettre un événement pour la recherche
      this.$emit('search', this.searchQuery)
    },
    scrollToSection(sectionId) {
      if (this.$route.path !== '/') {
        this.$router.push('/')
        setTimeout(() => {
          this.scrollToElement(sectionId)
        }, 100)
      } else {
        this.scrollToElement(sectionId)
      }
      this.isMobileMenuOpen = false
      this.closeSearch()
    },
    scrollToElement(elementId) {
      const element = document.getElementById(elementId)
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 100,
          behavior: 'smooth'
        })
      }
    },
    handleClickOutside(event) {
      const header = this.$el
      const isClickInside = header.contains(event.target)
      
      if (!isClickInside && this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false
      }
    }
  }
}
</script>

<style scoped>
header {
  background-color: white;
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  transition: var(--transition);
}

.logo:hover {
  transform: translateY(-2px);
}

.logo-img {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid var(--primary);
}

.logo-icon {
  font-size: 1.8rem;
  color: var(--primary);
}

.logo-text {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--dark);
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-links {
  display: flex;
  gap: 30px;
  align-items: center;
}

.nav-links a {
  color: var(--dark);
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: var(--transition);
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.nav-links a:hover {
  color: var(--primary);
}

.nav-links a::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  background: var(--primary);
  left: 0;
  bottom: -5px;
  transition: var(--transition);
}

.nav-links a:hover::after {
  width: 100%;
}

.search-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--dark);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-btn:hover {
  background: var(--gray-light);
  color: var(--primary);
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--dark);
  cursor: pointer;
  padding: 8px;
  border-radius: var(--radius);
  transition: var(--transition);
}

.mobile-menu-btn:hover {
  background: var(--gray-light);
}

/* Barre de recherche */
.search-container {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transform: translateY(-100%);
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 999;
}

.search-container.active {
  transform: translateY(0);
  opacity: 1;
}

.search-bar {
  padding: 20px 0;
  position: relative;
}

.search-bar input {
  width: 100%;
  padding: 12px 50px 12px 20px;
  border: 2px solid var(--gray-light);
  border-radius: var(--radius);
  font-size: 1rem;
  transition: var(--transition);
}

.search-bar input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-close {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray);
  cursor: pointer;
  padding: 12px 20px;
  font-size: 1.2rem;
  transition: var(--transition);
}

.search-close:hover {
  color: var(--danger);
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    background: white;
    flex-direction: column;
    padding: 20px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    gap: 20px;
    max-height: calc(100vh - 70px);
    overflow-y: auto;
    z-index: 1000;
  }

  .nav-links.active {
    display: flex;
  }

  .mobile-menu-btn {
    display: block;
  }
  
  .logo-text {
    font-size: 1.4rem;
  }
  
  .nav-links a {
    width: 100%;
    justify-content: center;
    padding: 10px;
    border-radius: var(--radius);
  }
  
  .nav-links a:hover {
    background: var(--gray-light);
  }
  
  .search-btn {
    margin-top: 10px;
  }
}

@media (max-width: 480px) {
  .logo-text {
    font-size: 1.2rem;
  }
  
  .logo-img {
    width: 32px;
    height: 32px;
  }
  
  .nav-container {
    padding: 10px 0;
  }
}
</style>